import { Link } from 'react-router-dom';
import { Play } from 'lucide-react';
import { cn } from '../../lib/cn';
import type { GameMeta } from '../../config/games';
import { Badge } from '../ui/primitives';

const VOLATILITY_TONE = {
  low: 'agave',
  medium: 'gold',
  high: 'crimson',
} as const;

export function GameCard({
  game,
  size = 'md',
  className,
}: {
  game: GameMeta;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}) {
  const comingSoon = game.status === 'coming-soon';

  return (
    <Link
      to={comingSoon ? '#' : `/games/${game.id}`}
      aria-disabled={comingSoon}
      onClick={(e) => comingSoon && e.preventDefault()}
      className={cn(
        'group panel panel-hover relative block overflow-hidden',
        comingSoon && 'cursor-not-allowed opacity-70',
        className,
      )}
    >
      <div
        className={cn(
          'relative flex items-center justify-center overflow-hidden',
          size === 'sm' ? 'h-24' : size === 'lg' ? 'h-44' : 'h-32',
        )}
        style={{ backgroundImage: `linear-gradient(140deg, ${game.art.from}, ${game.art.to})` }}
      >
        <div
          className="absolute inset-0 opacity-25 mix-blend-overlay"
          style={{
            backgroundImage:
              'radial-gradient(60% 90% at 30% 10%, rgba(255,255,255,0.55), transparent 60%), repeating-linear-gradient(120deg, rgba(0,0,0,0.35) 0 2px, transparent 2px 10px)',
          }}
        />
        {game.art.motif ? (
          <span className="absolute right-3 top-2 select-none text-4xl text-white/15" aria-hidden>
            {game.art.motif}
          </span>
        ) : null}
        <span
          className={cn(
            'relative select-none drop-shadow-[0_6px_12px_rgba(0,0,0,0.5)] transition-transform duration-300 group-hover:scale-110',
            size === 'sm' ? 'text-3xl' : size === 'lg' ? 'text-6xl' : 'text-5xl',
          )}
          aria-hidden
        >
          {game.art.glyph}
        </span>

        <div className="absolute left-2 top-2 flex flex-wrap gap-1">
          {game.isNew ? <Badge tone="sky">New</Badge> : null}
          {game.badges?.slice(0, 1).map((badge) => (
            <Badge key={badge} tone="gold">
              {badge}
            </Badge>
          ))}
        </div>

        {comingSoon ? (
          <div className="absolute inset-0 grid place-items-center bg-ink-1000/60">
            <Badge tone="neutral">Coming soon</Badge>
          </div>
        ) : (
          <div className="absolute inset-0 grid place-items-center bg-ink-1000/45 opacity-0 backdrop-blur-[2px] transition-opacity duration-200 group-hover:opacity-100">
            <span className="flex items-center gap-2 rounded-full bg-agave-500 px-4 py-2 text-sm font-semibold text-ink-1000 shadow-glow">
              <Play className="size-4 fill-current" /> Play
            </span>
          </div>
        )}
      </div>

      <div className="p-3.5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="truncate text-sm font-semibold text-white">{game.name}</h3>
            <p className="mt-0.5 truncate text-xs text-ink-400">{game.provider}</p>
          </div>
          <Badge tone={VOLATILITY_TONE[game.volatility]}>{game.volatility}</Badge>
        </div>
        <div className="mt-3 flex items-center justify-between text-[11px] text-ink-400">
          <span className="numeric">RTP {(game.rtp * 100).toFixed(2)}%</span>
          <span className="numeric">Max {game.maxWinMultiplier.toLocaleString()}x</span>
        </div>
      </div>
    </Link>
  );
}
